'use client';
import * as React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import useSlotProps from '@mui/utils/useSlotProps';
import { useNonNullableContext } from 'packages/toolpad/toolpad-utils/src/react';
import {
  Alert,
  Badge,
  Button,
  Snackbar,
  IconButton,
  CloseReason,
  SnackbarProps,
  SnackbarContent,
  SnackbarCloseReason,
} from '@mui/material';

import {
  useLocaleText,
  type LocaleText,
} from '../AppProvider/LocalizationProvider';

import { NotificationsContext } from './NotificationsContext';
import {
  ShowNotification,
  CloseNotification,
  ShowNotificationOptions,
} from './useNotifications';

export interface NotificationsProviderSlotProps {
  snackbar: SnackbarProps;
}

export interface NotificationsProviderSlots {
  /**
   * The component that renders the snackbar.
   * @default Snackbar
   */
  snackbar: React.ElementType;
}

const RootPropsContext = React.createContext<NotificationsProviderProps | null>(
  null
);

interface NotificationsProviderLocaleText {
  close: string;
}

const defaultLocaleText: Pick<
  LocaleText,
  keyof NotificationsProviderLocaleText
> = {
  close: 'Close',
};

interface NotificationProps {
  open: boolean;
  badge: string | null;
  notificationKey: string;
  message: React.ReactNode;
  options: ShowNotificationOptions;
}

function Notification({
  open,
  badge,
  message,
  options,
  notificationKey,
}: NotificationProps) {
  const globalLocaleText = useLocaleText();
  const localeText = { ...defaultLocaleText, ...globalLocaleText };
  const { close } = useNonNullableContext(NotificationsContext);

  const { severity, onAction, actionText, autoHideDuration } = options;

  const handleClose = React.useCallback(
    (event: unknown, reason?: CloseReason | SnackbarCloseReason) => {
      if (reason === 'clickaway') {
        return;
      }
      close(notificationKey);
    },
    [notificationKey, close]
  );

  const action = (
    <React.Fragment>
      {onAction ? (
        <Button size="small" color="inherit" onClick={onAction}>
          {actionText ?? 'Action'}
        </Button>
      ) : null}
      <IconButton
        size="small"
        color="inherit"
        onClick={handleClose}
        title={localeText?.close}
        aria-label={localeText?.close}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </React.Fragment>
  );

  const props = React.useContext(RootPropsContext);
  const SnackbarComponent = props?.slots?.snackbar ?? Snackbar;
  const snackbarSlotProps = useSlotProps({
    ownerState: props,
    elementType: SnackbarComponent,
    externalSlotProps: props?.slotProps?.snackbar,
    additionalProps: {
      open,
      action,
      autoHideDuration,
      onClose: handleClose,
    },
  });

  return (
    <SnackbarComponent key={notificationKey} {...snackbarSlotProps}>
      <Badge color="primary" badgeContent={badge} sx={{ width: '100%' }}>
        {severity ? (
          <Alert action={action} severity={severity} sx={{ width: '100%' }}>
            {message}
          </Alert>
        ) : (
          <SnackbarContent action={action} message={message} />
        )}
      </Badge>
    </SnackbarComponent>
  );
}

interface NotificationQueueEntry {
  open: boolean;
  notificationKey: string;
  message: React.ReactNode;
  options: ShowNotificationOptions;
}

interface NotificationsState {
  queue: NotificationQueueEntry[];
}

interface NotificationsProps {
  state: NotificationsState;
}

function Notifications({ state }: NotificationsProps) {
  const currentNotification = state.queue[0] ?? null;

  return currentNotification ? (
    <Notification
      {...currentNotification}
      badge={state.queue.length > 1 ? String(state.queue.length) : null}
    />
  ) : null;
}

export interface NotificationsProviderProps {
  children?: React.ReactNode;

  slots?: Partial<NotificationsProviderSlots>;

  slotProps?: Partial<NotificationsProviderSlotProps>;
}

let nextId = 0;
const generateId = () => {
  const id = nextId;
  nextId += 1;
  return id;
};

/**
 * Provider for Notifications. The subtree of this component can use the `useNotifications` hook to
 * access the notifications API. The notifications are shown in the same order they are requested.
 *
 * Demos:
 *
 * - [Sign-in Page](https://mui.com/toolpad/core/react-sign-in-page/)
 * - [useNotifications](https://mui.com/toolpad/core/react-use-notifications/)
 *
 * API:
 *
 * - [NotificationsProvider API](https://mui.com/toolpad/core/api/notifications-provider)
 */
function NotificationsProvider(props: NotificationsProviderProps) {
  const { children } = props;
  const [state, setState] = React.useState<NotificationsState>({ queue: [] });

  const show = React.useCallback<ShowNotification>((message, options = {}) => {
    const notificationKey =
      options.key ?? `::toolpad-internal::notification::${generateId()}`;
    setState((prev) => {
      if (prev.queue.some((n) => n.notificationKey === notificationKey)) {
        // deduplicate by key
        return prev;
      }
      return {
        ...prev,
        queue: [
          ...prev.queue,
          { message, options, open: true, notificationKey },
        ],
      };
    });
    return notificationKey;
  }, []);

  const close = React.useCallback<CloseNotification>((key) => {
    setState((prev) => ({
      ...prev,
      queue: prev.queue.filter((n) => n.notificationKey !== key),
    }));
  }, []);

  const contextValue = React.useMemo(() => ({ show, close }), [show, close]);

  return (
    <RootPropsContext.Provider value={props}>
      <NotificationsContext.Provider value={contextValue}>
        {children}
        <Notifications state={state} />
      </NotificationsContext.Provider>
    </RootPropsContext.Provider>
  );
}

export { NotificationsProvider };
